import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

interface VoiceRecorderProps {
  onTranscription: (text: string) => void;
  isListening: boolean;
  setIsListening: (listening: boolean) => void;
  disabled?: boolean;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscription,
  isListening,
  setIsListening,
  disabled = false
}) => {
  const [isStarting, setIsStarting] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onTranscriptionRef = useRef(onTranscription);
  const accumulatedTranscriptRef = useRef<string>('');
  const shouldSendOnEndRef = useRef(false);

  // Update the callback ref when it changes
  useEffect(() => {
    onTranscriptionRef.current = onTranscription;
  }, [onTranscription]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognitionClass();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-GB';
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onresult = (event) => {
        const lastResult = event.results[event.results.length - 1];
        const transcript = lastResult[0].transcript;

        // Accumulate all final results instead of sending immediately
        if (lastResult.isFinal && transcript.trim().length > 0) {
          accumulatedTranscriptRef.current += (accumulatedTranscriptRef.current ? ' ' : '') + transcript.trim();
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        if (event.error === 'not-allowed') {
          setIsStarting(false);
          setIsListening(false);
          toast.error('Microphone permission denied. Please allow microphone access.');
        } else if (event.error === 'network') {
          setIsStarting(false);
          setIsListening(false);
          toast.error('Network error. Please check your connection.');
        } else if (event.error === 'no-speech') {
          // Don't stop on no-speech, just notify
          toast.info('No speech detected. Keep speaking or click the mic to stop.');
        } else if (event.error === 'aborted') {
          // Ignore aborted errors when user manually stops
        } else {
          setIsStarting(false);
          setIsListening(false);
          toast.error('Voice input error. Please try again.');
        }
      };

      recognitionRef.current.onstart = () => {
        setIsStarting(false);
        setIsListening(true);
        accumulatedTranscriptRef.current = ''; // Reset on start
        shouldSendOnEndRef.current = false; // Reset send flag
        toast.success('Recording... Click the microphone button again when done to send your message.', {
          duration: 5000,
        });
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        setIsStarting(false);
        
        // Send accumulated transcript if user manually stopped
        if (shouldSendOnEndRef.current) {
          shouldSendOnEndRef.current = false;
          
          if (accumulatedTranscriptRef.current.trim().length > 0) {
            onTranscriptionRef.current(accumulatedTranscriptRef.current);
            accumulatedTranscriptRef.current = ''; // Reset for next recording
          } else {
            toast.info('No speech detected. Try speaking again.');
          }
        }
      };
    } else {
      console.warn('Speech recognition not supported');
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Recognition already stopped
        }
      }
    };
  }, []); // Empty dependency array - only run once

  const toggleListening = async () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser.');
      return;
    }

    if (isStarting) {
      return;
    }

    if (isListening) {
      // Set flag to send transcript when onend fires
      shouldSendOnEndRef.current = true;
      
      // Add delay to allow final speech processing
      toast.info('Processing your message...', { duration: 1500 });
      
      setTimeout(() => {
        try {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        } catch (e) {
          // Recognition already stopped
        }
      }, 800); // Give 800ms for final speech to be processed
      
      // Don't send here - let onend handle it after processing final results
    } else {
      try {
        if (restartTimeoutRef.current) {
          clearTimeout(restartTimeoutRef.current);
        }

        setIsStarting(true);
        
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        
        restartTimeoutRef.current = setTimeout(() => {
          try {
            if (recognitionRef.current) {
              recognitionRef.current.start();
            }
          } catch (error) {
            console.error('Error starting recognition:', error);
            setIsStarting(false);
            setIsListening(false);
            toast.error('Failed to start voice input. Please try again.');
          }
        }, 100);
        
      } catch (error) {
        console.error('Microphone permission error:', error);
        setIsStarting(false);
        setIsListening(false);
        toast.error('Microphone permission denied. Please allow microphone access.');
      }
    }
  };

  return (
    <Button
        variant="voice"
        size="voice"
        onClick={toggleListening}
        disabled={isStarting || disabled}
        className={`transition-therapy ${
          isListening 
            ? 'animate-pulse bg-primary/30 border-primary shadow-therapy' 
            : (isStarting || disabled)
            ? 'opacity-50 cursor-not-allowed' 
            : ''
        }`}
      >
        {isListening ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
      </Button>
  );
};